import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { api } from '../api';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];
const inputCls = "w-full px-4 py-3 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none border border-gray-200 bg-white";

export default function SubmitTestimonial() {
  const [form, setForm]       = useState({ name: '', role: '', company: '', quote: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [focused, setFocused] = useState('');

  const fieldStyle = (name) => ({
    borderColor: focused === name ? ACCENT : undefined,
    boxShadow: focused === name ? `0 0 0 3px rgba(94,14,215,0.10)` : undefined,
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
  });

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await api.submitTestimonial(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-16" style={{ background: '#FAFAF8', fontFamily: "'Inter', sans-serif" }}>
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center gap-2.5 mb-10">
          <img src="/logo.png" alt="Syenovix" className="w-9 h-9 rounded-xl object-cover" />
          <span className="text-sm font-bold tracking-widest uppercase" style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Syenovix</span>
        </div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(94,14,215,0.08)' }}>
              <CheckCircle2 size={28} style={{ color: ACCENT }} />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#18181B' }}>
              Thank You!
            </h2>
            <p className="text-gray-500 text-sm mb-6">Your testimonial has been submitted and is under review. We'll publish it once approved.</p>
            <Link to="/" className="text-sm font-semibold" style={{ color: ACCENT }}>← Back to Syenovix</Link>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }}>
            <h1 className="text-3xl font-bold mb-2 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#18181B' }}>
              Share Your Experience
            </h1>
            <p className="text-gray-500 text-sm mb-8">Tell us about your experience working with Syenovix. Testimonials are reviewed before publishing.</p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-1.5" style={{ color: '#6B6B72' }}>Full Name *</label>
                  <input required className={inputCls} style={fieldStyle('name')} value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Jane Doe" onFocus={()=>setFocused('name')} onBlur={()=>setFocused('')} />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-1.5" style={{ color: '#6B6B72' }}>Role</label>
                  <input className={inputCls} style={fieldStyle('role')} value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))} placeholder="CTO" onFocus={()=>setFocused('role')} onBlur={()=>setFocused('')} />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-1.5" style={{ color: '#6B6B72' }}>Company</label>
                <input className={inputCls} style={fieldStyle('company')} value={form.company} onChange={e=>setForm(p=>({...p,company:e.target.value}))} placeholder="Acme Inc." onFocus={()=>setFocused('company')} onBlur={()=>setFocused('')} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-1.5" style={{ color: '#6B6B72' }}>Your Testimonial *</label>
                <textarea required rows={5} className={`${inputCls} resize-none`} style={fieldStyle('quote')} value={form.quote} onChange={e=>setForm(p=>({...p,quote:e.target.value}))} placeholder="Share your experience working with Syenovix..." onFocus={()=>setFocused('quote')} onBlur={()=>setFocused('')} />
              </div>
              {error && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
              <motion.button type="submit" whileTap={{ scale: 0.97 }} disabled={loading} className="py-3.5 rounded-xl text-sm font-bold text-white tracking-wide" style={{ background: ACCENT, opacity: loading ? 0.8 : 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {loading ? 'Submitting...' : 'Submit Testimonial'}
              </motion.button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}
